/**
 * printSpec — v4 인쇄 규격 단일 출처 (실측 기반).
 *
 * 근거: kyhub_renewal /print-test 하네스의 현장 실측(2026-07, 캐논 6255).
 *  - 데드존(프린터 인쇄불가 영역): 상 5 / 하 5 / 좌 7 / 우 7 mm
 *  - A4(210×297):     안전여백 0mm · 본문 폰트 9pt
 *  - B4 JIS(257×364): 안전여백 3mm · 본문 폰트 11pt
 *    ※ CSS `size: B4` 키워드는 ISO(250×353)라 사용 금지 — 반드시 mm 명시.
 *
 * v2(구 printPreview, 328mm 가짜 캔버스 + 브라우저 축소 인쇄)와 달리 v4는 용지 실치수로
 * 렌더한다. v2의 구조 상수(헤더 120mm 등)는 콘텐츠 폭 비례계수 k로 환산해 시각 구성을
 * 유지한다(k = 실콘텐츠폭 / v2 콘텐츠폭 296mm). 본문 폰트만 실측값(pt)으로 고정.
 */

export const MM_PER_PX = 25.4 / 96;
export const PX_PER_MM = 96 / 25.4;

// 프린터 데드존 프로파일 (기본: 캐논 6255 실측). 프린터별로 조정 가능하도록 분리.
export const PRINTER_DEADZONE = { top: 5, bottom: 5, left: 7, right: 7 };

export const PAPERS_V4 = {
  A4: { key: 'A4', label: 'A4', w: 210, h: 297, safe: 0, contentFontPt: 9 },
  B4: { key: 'B4', label: 'B4', w: 257, h: 364, safe: 3, contentFontPt: 11 }, // JIS 257×364
};

// v2(328mm 캔버스) 설계 기준값 — k 환산의 분모
const V2_CONTENT_W_MM = 296; // 328 − 좌우 여백 16×2

/**
 * 용지 키 → 시트 지오메트리(전부 mm).
 * 콘텐츠 박스 = 용지 − 데드존 − 안전여백. 페이지 배치 상수는 v2 구조의 k 비례 환산.
 * 슬롯 높이 = 문제영역/2 (v2의 160/320.5·205/410.5 비율과 동일 규칙).
 */
export function getSheetGeometry(paperKey, deadzone = PRINTER_DEADZONE) {
  const paper = PAPERS_V4[paperKey] || PAPERS_V4.A4;
  const inset = {
    top: deadzone.top + paper.safe,
    bottom: deadzone.bottom + paper.safe,
    left: deadzone.left + paper.safe,
    right: deadzone.right + paper.safe,
  };
  const contentW = paper.w - inset.left - inset.right;
  const contentH = paper.h - inset.top - inset.bottom;
  const k = contentW / V2_CONTENT_W_MM;

  // 디자인 리뉴얼(2026-08, 시안 픽셀 실측):
  //  - A4(renewalA4): 컴팩트 헤더(문제 시작 paper 70.8) + 회색 프레임(기준선 paper 14)
  //  - B4(renewalB4): 수능 모의고사형 — 첫장 중앙 3줄 헤더·제2교시 캡슐·대형 페이지번호(홀짝 교차),
  //    검정 0.41mm 프레임(기준선 paper 18.1, 굵은 가로줄 첫장 62.5/이후 30.9, 문제영역 하단 334.1)
  const design = paperKey === 'A4' ? 'renewalA4' : 'renewalB4';

  let headerFirstH;
  let headerRestH;
  let restTopGap;
  let footerH;
  if (design === 'renewalA4') {
    headerFirstH = 70.8 - inset.top; // 문제 영역 시작(실측)
    footerH = 25 * k;
    headerRestH = footerH; // 2페이지 이후 상단 여백 = 하단 푸터 영역과 동일(대칭)
    restTopGap = 0;
  } else {
    // renewalB4: 문제 기본 시작 = 굵은 가로줄 + 5mm(첫장 62.5+5=67.5 / 이후 30.9+5=35.9).
    // 유형 마커가 붙은 문제만 마커 밴드만큼 내려간다(빈 예약 공간 없음).
    // 문제영역 하단(세로줄 끝) 334.1 — 전부 시안 실측.
    headerFirstH = 67.5 - inset.top;
    headerRestH = 35.9 - inset.top;
    restTopGap = 0;
    footerH = contentH - (334.1 - inset.top);
  }
  const colGap = 16 * k; // 2단 사이 간격(v2 비례 계승)
  // 리뉴얼 프레임: 헤더·푸터 좌우 기준선에 문제 영역 좌우를 수직 정렬.
  const frameInset = design === 'renewalA4' ? 14 - inset.left : 18.1 - inset.left;
  const colW = (contentW - frameInset * 2 - colGap) / 2;

  const areaFirstH = contentH - headerFirstH - footerH;
  const areaRestH = contentH - headerRestH - restTopGap - footerH;

  // 하단 문제 슬롯 지점(영역 대비 비율). 기본 1/2(v2 계승).
  // A4 리뉴얼은 시안 실측 역산 — 2번 문제 시작 paper 164.2mm → (164.2-70.8)/areaFirst = 0.456.
  const slotRatio = design === 'renewalA4' ? 0.456 : 0.5;

  return {
    paper,
    inset,
    contentW,
    contentH,
    k,
    design,
    frameInset,
    headerFirstH,
    headerRestH,
    restTopGap,
    footerH,
    colGap,
    colW,
    colWPx: colW * PX_PER_MM,
    areaFirstH,
    areaRestH,
    slotFirstH: areaFirstH * slotRatio,
    slotRestH: areaRestH * slotRatio,
    contentFontPt: paper.contentFontPt,
    // v2 pt 단위 요소의 환산 헬퍼 (헤더/푸터 등 구조 텍스트)
    pt: (v2pt) => v2pt * k,
  };
}
